import { useEffect, useRef } from "react";

type ReactiveGridProps = {
  className?: string;
  color?: string;
  cell?: number;
  radius?: number;
  strength?: number;
  variant?: "grid" | "crosses";
};

type Point = {
  x: number;
  y: number;
  ox: number;
  oy: number;
};

export function ReactiveGrid({
  className = "",
  color = "194, 242, 61",
  cell = 48,
  radius = 190,
  strength = 30,
  variant = "grid",
}: ReactiveGridProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let frame = 0;
    let running = true;
    let points: Point[][] = [];
    const pointer = { x: -1000, y: -1000, tx: -1000, ty: -1000, active: false };
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const build = () => {
      const rect = host.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const columns = Math.ceil(width / cell) + 2;
      const rows = Math.ceil(height / cell) + 2;
      points = Array.from({ length: rows }, (_, row) =>
        Array.from({ length: columns }, (_, column) => ({
          x: (column - 1) * cell,
          y: (row - 1) * cell,
          ox: 0,
          oy: 0,
        })),
      );
    };

    const move = (event: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      pointer.tx = event.clientX - rect.left;
      pointer.ty = event.clientY - rect.top;
      pointer.active = true;
    };
    const leave = () => {
      pointer.active = false;
      pointer.tx = -1000;
      pointer.ty = -1000;
    };

    const displace = (point: Point) => {
      const dx = point.x - pointer.x;
      const dy = point.y - pointer.y;
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = pointer.active && distance < radius ? Math.pow(1 - distance / radius, 2) : 0;
      const targetX = (dx / distance) * force * strength;
      const targetY = (dy / distance) * force * strength;
      point.ox += (targetX - point.ox) * 0.12;
      point.oy += (targetY - point.oy) * 0.12;
      return { x: point.x + point.ox, y: point.y + point.oy, force };
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);
      pointer.x += (pointer.tx - pointer.x) * 0.14;
      pointer.y += (pointer.ty - pointer.y) * 0.14;

      if (variant === "crosses") {
        points.flat().forEach((point) => {
          const position = displace(point);
          const size = 2.5 + position.force * 7;
          context.beginPath();
          context.moveTo(position.x - size, position.y);
          context.lineTo(position.x + size, position.y);
          context.moveTo(position.x, position.y - size);
          context.lineTo(position.x, position.y + size);
          context.strokeStyle = `rgba(${color}, ${0.16 + position.force * 0.48})`;
          context.lineWidth = 1;
          context.stroke();
        });
      } else {
        context.lineWidth = 1;
        points.forEach((row) => {
          context.beginPath();
          row.forEach((point, index) => {
            const position = displace(point);
            if (index === 0) context.moveTo(position.x, position.y);
            else context.lineTo(position.x, position.y);
          });
          context.strokeStyle = `rgba(${color}, 0.16)`;
          context.stroke();
        });
        for (let column = 0; column < (points[0]?.length ?? 0); column += 1) {
          context.beginPath();
          points.forEach((row, index) => {
            const position = displace(row[column]);
            if (index === 0) context.moveTo(position.x, position.y);
            else context.lineTo(position.x, position.y);
          });
          context.strokeStyle = `rgba(${color}, 0.13)`;
          context.stroke();
        }
      }

      if (!reduced && running) frame = requestAnimationFrame(draw);
    };

    const observer = new IntersectionObserver(([entry]) => {
      running = entry.isIntersecting;
      if (running && !reduced) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(draw);
      }
    });

    build();
    draw();
    observer.observe(host);
    const resizeObserver = new ResizeObserver(build);
    resizeObserver.observe(host);
    host.addEventListener("pointermove", move, { passive: true });
    host.addEventListener("pointerleave", leave);

    return () => {
      running = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      host.removeEventListener("pointermove", move);
      host.removeEventListener("pointerleave", leave);
    };
  }, [cell, color, radius, strength, variant]);

  return <canvas ref={canvasRef} className={`reactive-grid ${className}`.trim()} aria-hidden="true" />;
}
